/**
 * Ejecuta tareas con un techo de simultaneidad, entregando cada resultado
 * apenas está listo.
 *
 * Por qué existe: al partir una oficina en ~21 grupos, disparar las 21
 * peticiones a la vez no acelera nada — los motores de ruteo corren en **una
 * sola réplica** (Valhalla `replicas: 1` por su PVC `ReadWriteOnce`), así que
 * los problemas simultáneos se reparten los mismos cores y solo agregan riesgo
 * de memoria y timeouts en cascada. Con un techo bajo se aprovecha el
 * paralelismo real sin ahogar al motor.
 *
 * El callback `onSettled` se invoca por tarea a medida que termina (no al
 * final): es lo que permite pintar cada grupo apenas responde en vez de esperar
 * a que terminen todos.
 */

/** Resultado de una tarea: valor o error, nunca ambos. */
export type SettledResult<T> =
  | { status: 'fulfilled'; value: T; index: number }
  | { status: 'rejected'; error: unknown; index: number };

/**
 * Corre `tasks` con como máximo `limit` en vuelo simultáneamente.
 *
 * Una tarea que falla **no** aborta a las demás: se reporta como `rejected` y
 * la ejecución continúa. Los resultados se devuelven en el orden de `tasks`,
 * sin importar el orden en que terminaron.
 *
 * @param tasks funciones que arrancan la tarea al invocarse (no promesas ya
 *   iniciadas: si se pasaran promesas, ya estarían todas corriendo y el techo
 *   no serviría de nada).
 * @param limit máximo de tareas en vuelo (se normaliza a ≥ 1).
 * @param onSettled se invoca por cada tarea al terminar, con su resultado.
 */
export async function runWithLimit<T>(
  tasks: ReadonlyArray<() => Promise<T>>,
  limit: number,
  onSettled?: (result: SettledResult<T>) => void,
): Promise<SettledResult<T>[]> {
  const results: SettledResult<T>[] = new Array(tasks.length);
  const max = Math.max(1, Math.floor(limit));
  let next = 0;

  async function worker(): Promise<void> {
    // Cada worker toma el siguiente índice libre; con `max` workers nunca hay
    // más de `max` tareas en vuelo.
    while (next < tasks.length) {
      const index = next++;
      let settled: SettledResult<T>;
      try {
        settled = { status: 'fulfilled', value: await tasks[index](), index };
      } catch (error) {
        settled = { status: 'rejected', error, index };
      }
      results[index] = settled;
      onSettled?.(settled);
    }
  }

  await Promise.all(Array.from({ length: Math.min(max, tasks.length) }, () => worker()));
  return results;
}
